/**
 * tokens.ts — ค่าพื้นฐานของหน้าตาระบบ
 *
 * แนวทางการออกแบบ:
 *   - โครงสีเป็นเทากลางเกือบทั้งหมด ใช้สีเน้นเพียงสีเดียว
 *     สีที่เหลือสงวนไว้สื่อสถานะเท่านั้น (ผ่าน/รอ/ผิดพลาด) จะได้อ่านข้อมูลได้ไว
 *   - แทนเงาด้วยเส้นขอบบาง 1px ทำให้หน้าจอดูสงบและพิมพ์ออกมาสวย
 *   - ตัวเลขเงินใช้ tabular-nums เสมอ หลักจะเรียงตรงกันทุกแถว
 *   - สเกลระยะห่างเป็นทวีคูณของ 4px ทำให้จัดวางสม่ำเสมอโดยไม่ต้องคิดมาก
 *
 * ไฟล์นี้เป็นแหล่งความจริงเดียวของค่าเหล่านี้ ทั้ง Tailwind (เว็บ) และ
 * NativeWind (มือถือ) อ่านจากที่นี่ จึงไม่มีทางที่สองแพลตฟอร์มจะเพี้ยนจากกัน
 */

/**
 * สเกลสีเทากลาง
 * เลือกให้ตัวอักษรบนพื้นขาวได้คอนทราสต์ผ่าน WCAG AA
 * (neutral700 บน neutral0 = 8.6:1, neutral500 บน neutral0 = 4.8:1)
 */
export const neutral = {
  0: '#ffffff',
  50: '#f8f9fb',
  100: '#f1f3f6',
  200: '#e6e9ee',
  300: '#d3d8e0',
  400: '#a7aeba',
  500: '#6b7280',
  600: '#4b515e',
  700: '#343a45',
  800: '#22262e',
  900: '#16181d',
  950: '#0d0f13',
} as const;

/** สีเน้นเดียวของระบบ ใช้กับปุ่มหลัก ลิงก์ และตัวชี้สถานะที่กำลังทำงาน */
export const accent = {
  50: '#eef2fb',
  100: '#dae3f7',
  200: '#b6c7ef',
  300: '#8aa5e3',
  400: '#5a7fd4',
  500: '#3760c0',
  600: '#2a4ca6',
  700: '#223d86',
  800: '#1d3470',
  900: '#1a2c5c',
} as const;

/** สีสื่อสถานะ ใช้เท่าที่จำเป็น ไม่เอามาตกแต่ง */
export const semantic = {
  success: '#1a7f5a',
  successSoft: '#e7f5ef',
  warning: '#9a6206',
  warningSoft: '#fdf3e2',
  danger: '#b3261e',
  dangerSoft: '#fdeceb',
  info: accent[600],
  infoSoft: accent[50],
} as const;

/** ระยะห่าง หน่วย px บนสเกล 4pt */
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

/** ความโค้งมุม — โค้งพอให้ดูนุ่ม แต่ไม่กลมจนดูเป็นของเล่น */
export const radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  full: 9999,
} as const;

/**
 * ขนาดตัวอักษร หน่วย px คู่กับ line-height
 * ไม่มีขนาดเล็กกว่า 12px เพราะภาษาไทยมีสระบนล่าง ถ้าเล็กกว่านี้จะอ่านยาก
 */
export const fontSize = {
  xs: { size: 12, lineHeight: 18 },
  sm: { size: 13, lineHeight: 20 },
  base: { size: 15, lineHeight: 24 },
  lg: { size: 17, lineHeight: 26 },
  xl: { size: 20, lineHeight: 28 },
  '2xl': { size: 24, lineHeight: 32 },
  '3xl': { size: 30, lineHeight: 38 },
} as const;

export const fontWeight = {
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

/** ฟอนต์เดียวกับที่ใช้ในเอกสาร PDF เพื่อให้หน้าจอกับกระดาษดูเป็นระบบเดียวกัน */
export const fontFamily = {
  sans: "'IBM Plex Sans Thai', 'Noto Sans Thai', system-ui, -apple-system, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
} as const;

/**
 * เงา — ใช้น้อยมากและเบา สงวนไว้ให้ของที่ลอยอยู่จริง (dropdown, modal, toast)
 * ส่วนการ์ดและตารางใช้เส้นขอบแทน
 */
export const shadow = {
  none: 'none',
  sm: '0 1px 2px rgba(13, 15, 19, 0.06)',
  md: '0 4px 12px rgba(13, 15, 19, 0.08)',
  lg: '0 12px 32px rgba(13, 15, 19, 0.12)',
} as const;

/** จุดตัดขนาดหน้าจอ */
export const breakpoint = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/** ความเร็ว transition — เร็วพอให้รู้สึกตอบสนองทันที ไม่ช้าจนหน่วง */
export const duration = {
  fast: 120,
  base: 180,
  slow: 260,
} as const;

/** ความสูงของส่วนประกอบที่ต้องเท่ากันทั้งระบบ */
export const size = {
  /** ความสูงปุ่มและช่องกรอก — 36px กดง่ายบนเมาส์ */
  control: 36,
  /** ความสูงเป้ากดบนมือถือ ต้องไม่ต่ำกว่า 44px ตามแนวทาง accessibility */
  touchTarget: 44,
  /** ความสูงแถบหัวเว็บ */
  header: 56,
  /** ความกว้างเมนูข้าง */
  sidebar: 240,
  sidebarCollapsed: 64,
} as const;

/** สีของสถานะเอกสาร ใช้ตรงกันทั้ง badge ในเว็บและมือถือ */
export const statusColor = {
  draft: { fg: neutral[600], bg: neutral[100] },
  sent: { fg: accent[700], bg: accent[50] },
  accepted: { fg: semantic.success, bg: semantic.successSoft },
  rejected: { fg: semantic.danger, bg: semantic.dangerSoft },
  issued: { fg: accent[700], bg: accent[50] },
  paid: { fg: semantic.success, bg: semantic.successSoft },
  cancelled: { fg: neutral[500], bg: neutral[100] },
} as const;

export type StatusKey = keyof typeof statusColor;
